import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreIndustriesComponent } from '../explore-more-industries/explore-more-industries.component';
import { trigger, transition, query, style, stagger, animate } from '@angular/animations';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface CaseStudy {
  image: string;
  title: string;
  description: string;
}
@Component({
  selector: 'app-healthcare',
  imports: [CommonModule, RouterModule, FaqComponent, ExploreMoreIndustriesComponent],
  standalone: true,
  templateUrl: './healthcare.component.html',
  styleUrl: './healthcare.component.scss',
  animations: [
    trigger('fadeInUpStagger', [
      transition('* => visible', [
        query('.animate-item', [
          style({ opacity: 0, transform: 'translateY(30px)' }),
          stagger('200ms', [
            animate('800ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})

export class HealthcareComponent implements AfterViewInit, OnInit, OnDestroy {
  private jsonLdScript: any;
  @ViewChild('impactSection') impactSection!: ElementRef;
  impactSectionVisible = false;
  private observer: IntersectionObserver | null = null;

  caseStudies: CaseStudy[] = [
    {
      image: '../../../../assets/industries/healthcare/healthcare1.png',
      title: 'Healthcare Integration Engine for Secure Workflow Automation',
      description: 'The project addressed fragmented workflows and inefficient resource management by developing a centralized integration engine with secure single sign-on, streamlining data exchange across multiple systems, improving operational efficiency, and modernizing healthcare communication and workflows for better patient care and organizational agility.'
    },
    {
      image: '../../../../assets/industries/healthcare/healthcare2.png', // Replace with different image if available
      title: 'Data-Driven Mucormycosis Tracking App',
      description: 'Designed to combat the surge of mucormycosis (black fungus) cases, this medical app enables clinicians to systematically capture and analyze anonymized patient data while guiding patients with clear, symptom-led journeys. It brings together OTP-based registration, role-based access for doctors, patients, and admins, and smart symptom analysis to identify potential risk patterns and sources of infection faster. Integrated educational content ensures that patients and caregivers understand the disease, while structured data collection supports better clinical decisions and more targeted public health interventions.'
    },
    {
      image: '../../../../assets/industries/healthcare/healthcare3.png', // Replace with different image if available
      title: 'Virtual Health Assistant App',
      description: 'Kalpita Technologies partnered with healthcare organizations to build a mobile-first virtual assistant that streamlines how patients access care, manage appointments, and stay on top of their treatment plans. The solution unifies appointment scheduling, e‑prescriptions, medical records, reminders, payments, and video consultations into a secure, patient‑centric experience that reduces admin friction for providers while improving continuity of care for patients.'
    }
  ];

  healthCareFaqs: FaqItem[] = [
    {
      question: 'Why do healthcare providers need custom software solutions?',
      answer: `Off-the-shelf solutions often lack the flexibility to adapt to your unique clinical workflows, require expensive customizations, and create vendor lock-in. Custom healthcare software ensures: 
      Perfect-fit functionality tailored to your specialties and processes 
      Regulatory compliance (HIPAA, HITECH, GDPR) built from the ground up 
      Seamless integration with your existing EHR, PACS, and LIS systems 
      Competitive differentiation through proprietary features and patient experiences`
    },
    {
      question: 'Are your HealthTech solutions compliant with healthcare regulations?',
      answer: `Yes, our healthcare solutions follow global standards such as HIPAA, HL7/FHIR, 
      and GDPR to ensure data security and regulatory compliance. We maintain rigorous compliance 
      standards and the following accreditations: ISO 27001, SOC 2 Type II.`
    },
    {
      question: 'Can you integrate with existing hospital systems?',
      answer: `Absolutely. Our team specializes in seamless integration with EHRs, billing systems, 
      IoT devices, and other healthcare infrastructures. `
    },
    {
      question: 'What is AI-accelerated software development?',
      answer: `AI-accelerated development uses artificial intelligence to speed up architecture design, 
      code generation, testing, and integration - reducing development time by 50-60% while maintaining 
      high quality standards. This approach allows businesses to launch new capabilities in weeks or 
      months rather than years.`
    },
    {
      question: 'How quickly can you deliver a custom HealthTech solution?',
      answer: `Timelines vary by project scope, but our AI-accelerated approach typically delivers 
      production-ready solutions in 6-9 months for comprehensive platforms - 60% faster than 
      traditional development. We provide realistic timelines during initial consultation based 
      on your specific requirements.`
    }
  ];

  currentSlide = 0;
  isDragging = false;
  startX = 0;
  currentX = 0;
  offset = 0;
  autoSlideInterval: any;
  readonly SLIDE_DURATION = 5000;

  getTransform() {
    return `translateX(calc(${-this.currentSlide * 100}% + ${this.offset}px))`;
  }

  constructor(private router: Router, private modalService: NgbModal,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.stopAutoSlide();
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  ngAfterViewInit(): void {
    // 4. Set up Intersection Observer
    const options = {
      root: null,
      threshold: 0.2,
      rootMargin: '0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.impactSectionVisible = true;
          this.observer?.disconnect();
        }
      });
    }, options);

    if (this.impactSection) {
      this.observer.observe(this.impactSection.nativeElement);
    }

    // 5. Initialize 3D Tilt Effect
    this.setupTiltEffect();
  }

  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.solution-image-card, .impact-image-wrapper, .case-study-image-wrapper');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;
        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  getStarted(): void {

  }

  startAutoSlide() {
    this.stopAutoSlide();
    this.autoSlideInterval = setInterval(() => {
      this.nextSlide();
    }, this.SLIDE_DURATION);
  }

  stopAutoSlide() {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }

  nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.caseStudies.length;
  }

  goToSlide(index: number) {
    this.currentSlide = index;
    this.offset = 0;
    this.startAutoSlide();
  }


  // UX: Pause slider when user hovers to read text
  onMouseEnter() {
    this.stopAutoSlide();
  }

  onMouseLeave() {
    this.startAutoSlide();
  }
  // Mouse event handlers
  onMouseDown(event: MouseEvent) {
    this.isDragging = true;
    this.startX = event.clientX;
    this.stopAutoSlide();
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    this.currentX = event.clientX;
    this.offset = this.currentX - this.startX;
  }

  onMouseUp() {
    if (!this.isDragging) return;

    if (this.offset < -80 && this.currentSlide < this.caseStudies.length - 1) {
      this.currentSlide++;
    } else if (this.offset > 80 && this.currentSlide > 0) {
      this.currentSlide--;
    }

    this.isDragging = false;
    this.offset = 0;
    this.startAutoSlide();
  }
  // Touch event handlers
  onTouchStart(event: TouchEvent) {
    this.isDragging = true;
    this.startX = event.touches[0].clientX;
    this.stopAutoSlide();
  }

  onTouchMove(event: TouchEvent) {
    if (!this.isDragging) return;
    this.currentX = event.touches[0].clientX;
    this.offset = this.currentX - this.startX;
  }

  onTouchEnd() {
    this.onMouseUp();
  }

  redirectToAbout(): void {
    this.router.navigate(['/about-us']);
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.startAutoSlide();
  }
  injectSchemaMarkup(): void {
    const healthtechSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/industries/healthtech#webpage",
          "url": "https://www.kalpitatechnologies.com/industries/healthtech",
          "name": "HealthTech Software Development | AI-Powered Healthcare Solutions | Kalpita Technologies",
          "description": "AI-powered healthcare software development solutions by Kalpita Technologies. HIPAA, HL7/FHIR, GDPR compliant custom solutions.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/industries/healthtech#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Industries", "item": "https://www.kalpitatechnologies.com/industries" },
            { "@type": "ListItem", "position": 3, "name": "HealthTech", "item": "https://www.kalpitatechnologies.com/industries/healthtech" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/industries/healthtech#service",
          "name": "HealthTech Software Development Solutions",
          "serviceType": "Healthcare Software Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "End-to-end healthcare software development services delivering AI-powered solutions for hospitals, clinics, and digital health providers..."
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/industries/healthtech#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Are your HealthTech solutions compliant with healthcare regulations?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, our healthcare solutions follow global standards such as HIPAA, HL7/FHIR, and GDPR to ensure data security and regulatory compliance."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(healthtechSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  onCardClick(index: number): void {
    console.log('Clicked card', index);
    // navigate or open modal
  }

  getStarArray(rating: number): boolean[] {
    return Array(5).fill(false).map((_, index) => index < rating);
  }

  bookAFreeCall(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }
  testimonials(): void {
    this.router.navigate(['/'], { fragment: 'testimonials' });
  }
}
